import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CareTimeEditor } from './NoteTimingMeta';
import {
  getCurrentCareTime,
  getDefaultCareDayOffset,
} from '../utils/lateEntry';
import { useLanguage } from '../contexts/LanguageContext';

export default function AddendumModal({
  open,
  onClose,
  onSave,
  saving = false,
}) {
  const { t } = useLanguage();
  const [text, setText] = useState('');
  const [careTime, setCareTime] = useState(() => getCurrentCareTime());
  const [careDayOffset, setCareDayOffset] = useState(() => getDefaultCareDayOffset());
  const [documentationTime] = useState(() => new Date().toISOString());
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setText('');
    setCareTime(getCurrentCareTime());
    setCareDayOffset(getDefaultCareDayOffset());
    setError('');
  }, [open]);

  if (!open) return null;

  const handleSave = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError(t('addAddendumRequired'));
      return;
    }
    onSave?.({
      text: trimmed,
      care_time: careTime,
      care_day_offset: careDayOffset,
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="addendum-title"
        className="w-full max-w-lg rounded-2xl bg-white p-4 shadow-xl"
      >
        <h2 id="addendum-title" className="text-lg font-bold text-gray-900">
          {t('addAddendum')}
        </h2>
        <p className="mt-1 text-sm text-gray-500">{t('addAddendumSubtitle')}</p>

        <CareTimeEditor
          className="mt-3"
          careTime={careTime}
          careDayOffset={careDayOffset}
          documentationTime={documentationTime}
          onChange={(timing) => {
            setCareTime(timing.care_time);
            setCareDayOffset(timing.care_day_offset);
          }}
        />

        <label htmlFor="addendum-text" className="sr-only">
          {t('addAddendum')}
        </label>
        <textarea
          id="addendum-text"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (error) setError('');
          }}
          rows={8}
          placeholder={t('addAddendumPlaceholder')}
          className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-3 text-sm text-gray-900 outline-none focus:border-primary"
        />

        {error ? <p className="mt-2 text-sm text-critical">{error}</p> : null}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-full border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-60"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? t('saving') : t('saveAddendum')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
